import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { AuthenticationStore } from '../states/auth/auth.store';
import { environment } from '../../../environments/environment';

const AUTH_URLS = [
  environment.user.auth,
  environment.user.register,
  environment.user.refresh,
  environment.user.logout,
  environment.user.validate,
];

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);
  private readonly authStore = inject(AuthenticationStore);

  private isRefreshing = false;
  private refreshSubject = new BehaviorSubject<boolean | null>(null);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const cloned = this.withAuth(req);

    return next.handle(cloned).pipe(
      catchError(err => {
        const isAuthEndpoint = AUTH_URLS.some(url => cloned.url.startsWith(url));
        if (err instanceof HttpErrorResponse && err.status === 401 && !isAuthEndpoint) {
          return this.handle401(cloned, next);
        }
        return throwError(() => err);
      }),
    );
  }

  private withAuth(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const accessToken = this.authStore.authToken()?.accessToken;
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined;

    return req.clone({
      withCredentials: true,
      setHeaders: headers,
    });
  }

  private handle401(request: HttpRequest<unknown>, next: HttpHandler) {
    if (this.authService.isLogoutInProgress()) {
      return throwError(() => new Error('Logout in progress'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap(response => {
          this.authStore.storeAuthToken(response.authToken);
          this.isRefreshing = false;
          this.refreshSubject.next(true);
          return next.handle(this.withAuth(request));
        }),
        catchError(() => {
          this.isRefreshing = false;
          this.authService.logout();
          return throwError(() => new HttpErrorResponse({
            status: 401,
            statusText: 'Session expired',
            error: { message: 'Your session has expired. Please sign in again.' },
          }));
        }),
      );
    }

    return this.refreshSubject.pipe(
      filter(x => x !== null),
      take(1),
      switchMap(() => next.handle(this.withAuth(request))),
    );
  }
}
